import React, { useMemo } from "react";
import { Box, Typography, Card, CardContent, Grid } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LabelList,
  Tooltip,
} from "recharts";
import { format } from "date-fns";
import { AD_TYPES } from "../AdsSetupManager/functions";
import { CAPTURE_SOURCES } from "../../constants";

const UserActivations = ({ userData, selectedStartDate, selectedEndDate }) => {
  // Process real userData to generate daily registrations
  const dailyRegistrationsData = useMemo(() => {
    if (!userData || userData.length === 0) return [];

    // Filter data by date range if provided
    let filteredData = userData;
    if (selectedStartDate && selectedEndDate) {
      const startOfStartDate = new Date(selectedStartDate);
      startOfStartDate.setHours(0, 0, 0, 0);
      const endOfEndDate = new Date(selectedEndDate);
      endOfEndDate.setHours(23, 59, 59, 999);
      
      filteredData = userData.filter((user) => {
        const userDate = new Date(user.saveDate * 1000);
        const userDateTime = userDate.getTime();
        const startDateTime = startOfStartDate.getTime();
        const endDateTime = endOfEndDate.getTime();
        
        return userDateTime >= startDateTime && userDateTime <= endDateTime;
      });
    }

    // Group by date and count registrations
    const dailyRegistrations = {};
    
    filteredData.forEach((user) => {
      if (user.saveDate) {
        const date = new Date(user.saveDate * 1000);
        const dateKey = format(date, "MMM dd");
        
        if (dailyRegistrations[dateKey]) {
          dailyRegistrations[dateKey]++;
        } else {
          dailyRegistrations[dateKey] = 1;
        }
      }
    });

    // Convert to array format for chart
    return Object.entries(dailyRegistrations)
      .map(([date, registrations]) => ({
        date,
        registrations,
      }))
      .sort((a, b) => {
        // Sort by date
        const dateA = new Date(a.date + " 2024");
        const dateB = new Date(b.date + " 2024");
        return dateA - dateB;
      });
  }, [userData, selectedStartDate, selectedEndDate]);

  // Process real userData to generate activation sources
  const activationSourcesData = useMemo(() => {
    if (!userData || userData.length === 0) return [];

    // Filter data by date range if provided
    let filteredData = userData;
    if (selectedStartDate && selectedEndDate) {
      const startOfStartDate = new Date(selectedStartDate);
      startOfStartDate.setHours(0, 0, 0, 0);
      const endOfEndDate = new Date(selectedEndDate);
      endOfEndDate.setHours(23, 59, 59, 999);
      
      filteredData = userData.filter((user) => {
        const userDate = new Date(user.saveDate * 1000);
        const userDateTime = userDate.getTime();
        const startDateTime = startOfStartDate.getTime();
        const endDateTime = endOfEndDate.getTime();
        
        return userDateTime >= startDateTime && userDateTime <= endDateTime;
      });
    }

    // Count activation sources
    const sourceCounts = {};
    const sourceColors = {
      [CAPTURE_SOURCES.LIVE_CAM_LOGIN]: "#24b8e7",
      [CAPTURE_SOURCES.SOCIAL_LOGIN]: "#4285F4",
      [AD_TYPES.POST_EVENT]: "#00c853",
      [AD_TYPES.POST_GAME]: "#9c27b0",
      [CAPTURE_SOURCES.GAMES_LOGIN]: "#F4B400",
    };

    const sourceNames = {
      [CAPTURE_SOURCES.LIVE_CAM_LOGIN]: "Live Cam Login",
      [CAPTURE_SOURCES.SOCIAL_LOGIN]: "Social Login",
      [AD_TYPES.POST_EVENT]: "Post Event",
      [AD_TYPES.POST_GAME]: "Post Game",
      [CAPTURE_SOURCES.GAMES_LOGIN]: "Games Polls Login",
    };

    filteredData.forEach((user) => {
      if (user.captureSource) {
        if (sourceCounts[user.captureSource]) {
          sourceCounts[user.captureSource]++;
        } else {
          sourceCounts[user.captureSource] = 1;
        }
      }
    });

    const totalUsers = filteredData.length;
    
    // Convert to percentage and array format for pie chart
    return Object.entries(sourceCounts)
      .map(([source, count]) => ({
        name: sourceNames[source] || source,
        value: totalUsers > 0 ? Math.round((count / totalUsers) * 100) : 0,
        color: sourceColors[source] || "#666",
        count,
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value); // Sort by percentage descending
  }, [userData, selectedStartDate, selectedEndDate]);

  // Custom label function for pie chart
  const renderCustomizedLabel = (entry) => {
    if (entry.value >= 15) {
      return `${entry.name} ${entry.value}%`;
    }
    return `${entry.value}%`;
  };

  // Custom tooltip for pie chart
  const renderPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "#1a1b23",
            border: "1px solid #333",
            borderRadius: "8px",
            padding: "10px",
            color: "white",
          }}
        >
          <p style={{ margin: 0, color: "#24b8e7", fontWeight: "bold" }}>
            {data.name}
          </p>
          <p style={{ margin: "4px 0 0 0" }}>
            Count: {data.count} user{data.count !== 1 ? 's' : ''}
          </p>
          <p style={{ margin: "4px 0 0 0" }}>
            Percentage: {data.value}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Daily Registrations Chart */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              backgroundColor: "#1a1b23",
              border: "1px solid #333",
              height: "400px",
            }}
          >
            <CardContent sx={{ height: "100%", position: "relative" }}>
              <Typography variant="h6" sx={{ color: "white", mb: 1, fontSize: "20px", fontWeight: "600" }}>
                Daily Registrations
              </Typography>
              <Typography variant="body2" sx={{ color: "#9e9e9e", mb: 3, fontSize: "14px" }}>
                User registration trends over time
              </Typography>
              
              {/* Bar Chart using Recharts */}
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={dailyRegistrationsData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9e9e9e", fontSize: 12 }}
                  />
                  <YAxis 
                    domain={[0, 'dataMax + 1']}
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9e9e9e", fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1a1b23",
                      border: "1px solid #333",
                      borderRadius: "8px",
                      color: "white",
                    }}
                    labelStyle={{ color: "#24b8e7" }}
                    formatter={(value, name) => [
                      `${value} registration${value !== 1 ? 's' : ''}`,
                      "Registrations"
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Bar 
                    dataKey="registrations" 
                    fill="#24b8e7"
                    radius={[4, 4, 0, 0]}
                    barSize={60}
                  >
                    <LabelList 
                      dataKey="registrations" 
                      position="top" 
                      style={{ fill: "white", fontSize: "14px", fontWeight: "500" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              
              {/* Show message if no data */}
              {dailyRegistrationsData.length === 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    color: "#9e9e9e",
                  }}
                >
                  <Typography variant="body2">
                    No registration data available for the selected period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Activation Sources Pie Chart */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              backgroundColor: "#1a1b23",
              border: "1px solid #333",
              height: "400px",
            }}
          >
            <CardContent sx={{ height: "100%", position: "relative" }}>
              <Typography variant="h6" sx={{ color: "white", mb: 1, fontSize: "20px", fontWeight: "600" }}>
                Activation Sources
              </Typography>
              <Typography variant="body2" sx={{ color: "#9e9e9e", mb: 3, fontSize: "14px" }}>
                Distribution of user acquisition channels
              </Typography>
              
              {/* Pie Chart using Recharts */}
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={activationSourcesData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={renderCustomizedLabel}
                    labelLine={false}
                  >
                    {activationSourcesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={renderPieTooltip} />
                </PieChart>
              </ResponsiveContainer>
              
              {/* Show message if no data */}
              {activationSourcesData.length === 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    color: "#9e9e9e",
                  }}
                >
                  <Typography variant="body2">
                    No activation source data available for the selected period
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
export default UserActivations;